import { inject, Injectable, signal } from '@angular/core';
import { ClockSyncService } from './clock-sync.service';

/**
 * A single shared "now" signal, ticking on a fixed interval instead of being read fresh on
 * every change-detection pass. RelativeTimePipe takes this as an explicit pipe argument so
 * two evaluations of the same template expression within one check always agree on "now" --
 * reading a live clock directly inside an impure pipe let two evaluations straddle a minute
 * boundary and trip NG0100 (ExpressionChangedAfterItHasBeenChecked).
 *
 * Reads through ClockSyncService.now() (Date.now() + the measured server offset) rather
 * than the local Date.now() directly, so a device with a skewed clock still shows accurate
 * "N min ago" text against server-stamped timestamps. Note this only corrects *display*:
 * entity writes (interactions/addresses/leads created_at/occurred_at, etc.) still stamp
 * plain Date.now() and aren't offset-corrected -- see clock-sync.service.ts's own doc
 * comment ("Use this instead of Date.now() for all sync-related timestamps"), which nothing
 * currently follows. Applying the offset to those writes too is a separate, wider-reaching
 * change left for a future enhancement.
 */
@Injectable({ providedIn: 'root' })
export class ClockService {
  private clockSync = inject(ClockSyncService);
  private nowSignal = signal(this.clockSync.now());
  readonly now = this.nowSignal.asReadonly();

  constructor() {
    setInterval(() => this.nowSignal.set(this.clockSync.now()), 30_000);
  }
}
